import { useState } from 'react';
import { cn } from '@rocco/utils/cn';
import { Checkbox } from '@rocco/components/checkbox';
import type { AwardsListItem } from '@modules-rocco/filters/filter-state/awards-filter-state';

export interface AwardsTreeLeaf {
    label: string;
    value: string;
}

export interface AwardsTreeGroup {
    label: string;
    value: string;
    /** Single-leaf groups render as a plain checkbox row. */
    leaves: AwardsTreeLeaf[];
}

/**
 * Group flat award list items under their institutions, mirroring the legacy
 * tree: institutions with several awards become an expandable group, while
 * single awards (or awards without an institution) stay flat rows.
 */
export const groupAwardItems = (items: AwardsListItem[]): AwardsTreeGroup[] => {
    const groups: AwardsTreeGroup[] = [];
    const groupsByInstitution = new Map<string, AwardsTreeGroup>();

    for (const item of items) {
        const leaf: AwardsTreeLeaf = {
            label: item.label ?? String(item.value),
            value: String(item.value),
        };

        if (!item.institutionValue) {
            groups.push({
                label: leaf.label,
                value: leaf.value,
                leaves: [leaf],
            });
            continue;
        }

        const existing = groupsByInstitution.get(item.institutionValue);
        if (existing) {
            existing.leaves.push(leaf);
            continue;
        }

        const group: AwardsTreeGroup = {
            label: item.institutionLabel ?? item.institutionValue,
            value: item.institutionValue,
            leaves: [leaf],
        };
        groupsByInstitution.set(item.institutionValue, group);
        groups.push(group);
    }

    return groups;
};

interface AwardsCheckboxTreeProps {
    groups: AwardsTreeGroup[];
    checkedValues: ReadonlySet<string>;
    /** Called with the leaf values affected and their new checked state. */
    onToggle: (values: string[], checked: boolean) => void;
    isDark?: boolean;
}

const checkboxClassName = (isDark: boolean) =>
    cn(
        'mt-0.5 size-[0.9375rem] shrink-0 rounded-[0.125rem] border',
        isDark
            ? 'border-white/60 data-[state=checked]:border-white'
            : 'border-[#343434]',
    );

const labelClassName = (isDark: boolean) =>
    cn(
        'text-[0.875rem] leading-[1.358]',
        isDark ? 'text-white/80' : 'text-[#343434]',
    );

const LeafRow = ({
    leaf,
    checked,
    onToggle,
    isDark,
}: {
    leaf: AwardsTreeLeaf;
    checked: boolean;
    onToggle: (values: string[], checked: boolean) => void;
    isDark: boolean;
}) => (
    <label className="flex cursor-pointer items-start gap-2">
        <Checkbox
            checked={checked}
            onCheckedChange={(next) => onToggle([leaf.value], next === true)}
            className={checkboxClassName(isDark)}
        />
        <span className={labelClassName(isDark)}>{leaf.label}</span>
    </label>
);

const GroupRow = ({
    group,
    checkedValues,
    onToggle,
    isDark,
}: {
    group: AwardsTreeGroup;
    checkedValues: ReadonlySet<string>;
    onToggle: (values: string[], checked: boolean) => void;
    isDark: boolean;
}) => {
    const leafValues = group.leaves.map((leaf) => leaf.value);
    const checkedCount = leafValues.filter((value) =>
        checkedValues.has(value),
    ).length;
    const allChecked = checkedCount === leafValues.length;
    const [expanded, setExpanded] = useState(checkedCount > 0);

    return (
        <div className="flex flex-col gap-1.5">
            <div className="flex items-start justify-between gap-2">
                <label className="flex cursor-pointer items-start gap-2">
                    <Checkbox
                        checked={
                            allChecked
                                ? true
                                : checkedCount > 0
                                  ? 'indeterminate'
                                  : false
                        }
                        onCheckedChange={(next) =>
                            onToggle(leafValues, next === true)
                        }
                        className={checkboxClassName(isDark)}
                    />
                    <span className={labelClassName(isDark)}>
                        {group.label}
                    </span>
                </label>
                <button
                    type="button"
                    aria-expanded={expanded}
                    aria-label={`${expanded ? 'Hide' : 'Show'} ${group.label} awards`}
                    onClick={() => setExpanded((prev) => !prev)}
                    className={cn(
                        'shrink-0 p-0.5',
                        isDark ? 'text-white/80' : 'text-[#343434]',
                    )}
                >
                    <svg
                        width="14"
                        height="14"
                        viewBox="0 0 14 14"
                        fill="none"
                        aria-hidden="true"
                        className={cn(
                            'transition-transform duration-200',
                            expanded && '-scale-y-100',
                        )}
                    >
                        <path
                            d="M3 5l4 4 4-4"
                            stroke="currentColor"
                            strokeWidth="1.3"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        />
                    </svg>
                </button>
            </div>
            {expanded && (
                <div className="flex flex-col gap-1.5 pl-6">
                    {group.leaves.map((leaf) => (
                        <LeafRow
                            key={leaf.value}
                            leaf={leaf}
                            checked={checkedValues.has(leaf.value)}
                            onToggle={onToggle}
                            isDark={isDark}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

/**
 * Accessible replacement for the legacy react-checkbox-tree awards filter:
 * every checkbox is a real labelled control and the expand toggle is a
 * separate button, so no interactive element nests another.
 */
export const AwardsCheckboxTree = ({
    groups,
    checkedValues,
    onToggle,
    isDark = false,
}: AwardsCheckboxTreeProps) => (
    <div className="flex flex-col gap-1.5">
        {groups.map((group) =>
            group.leaves.length === 1 ? (
                <LeafRow
                    key={group.value}
                    leaf={group.leaves[0]!}
                    checked={checkedValues.has(group.leaves[0]!.value)}
                    onToggle={onToggle}
                    isDark={isDark}
                />
            ) : (
                <GroupRow
                    key={group.value}
                    group={group}
                    checkedValues={checkedValues}
                    onToggle={onToggle}
                    isDark={isDark}
                />
            ),
        )}
    </div>
);
